import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  IconButton,
  Chip,
  AppBar,
  Toolbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Card,
  InputAdornment,
  Skeleton,
  Slider,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  Checkbox,
  FormControlLabel,
  Divider,
} from '@mui/material';
import {
  ArrowBack,
  Search,
  Edit,
  Delete,
  FilterList,
  Refresh,
  Add,
  Coffee,
  AttachMoney,
  QrCode2,
  Download,
  Link as LinkIcon,
  Star,
  StarBorder,
  Person,
  LockReset,
  LocalOffer,
  Event,
} from '@mui/icons-material';
import axios from 'axios';
import MapSelector from '../components/MapSelector';
import PhotoManager from '../components/PhotoManager';

interface OwnerInfo {
  ownerId: string;
  ownerName: string;
  ownerEmail: string;
  subscriptionStatus: 'active' | 'expired' | 'none';
  subscriptionPlan: string | null;
  subscriptionEndDate: Date | string | null;
  hasActiveSubscription: boolean;
}

interface CafeStats {
  total: number;
  premium: number;
  withOwner: number;
  withoutOwner: number;
}

interface PartnerCafe {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
  address: string;
  priceLevel: number;
  isActive: boolean;
  allowsAnonymousMatching: boolean;
  addedDate: Date | string;
  photoURLs: string[];
  features?: string[];
  franchiseId?: string | null;
  // Premium/Owner bilgileri
  hasOwner?: boolean;
  isPremium?: boolean;
  ownerInfo?: OwnerInfo | null;
}

interface DropdownFranchise {
  id: string;
  name: string;
}

// Available features with icons (maksimum 8 seçilebilir)
const CAFE_FEATURES = [
  { key: 'wifi', label: 'Ücretsiz WiFi', icon: '📶' },
  { key: 'priz', label: 'Bol Priz', icon: '🔌' },
  { key: 'kartlaOdeme', label: 'Kartla Ödeme', icon: '💳' },
  { key: 'terrace', label: 'Teras', icon: '☀️' },
  { key: 'parking', label: 'Otopark', icon: '🅿️' },
  { key: 'petFriendly', label: 'Evcil Hayvan Dostu', icon: '🐾' },
  { key: 'quietArea', label: 'Sessiz Çalışma Alanı', icon: '🤫' },
  { key: 'outdoorSeating', label: 'Açık Hava Oturma', icon: '🌳' },
  { key: 'airConditioning', label: 'Klima', icon: '❄️' },
  { key: 'meetingRoom', label: 'Toplantı Odası', icon: '👥' },
  { key: 'fastWifi', label: 'Hızlı WiFi', icon: '⚡' },
  { key: 'breakfast', label: 'Kahvaltı Servisi', icon: '☕' },
  { key: 'longTables', label: 'Uzun Masa', icon: '📏' },
  { key: 'chargingStation', label: 'Şarj İstasyonu', icon: '🔋' },
  { key: 'liveMusic', label: 'Canlı Müzik', icon: '🎵' },
];

const MAX_FEATURES = 8;

const PartnerCafesPage: React.FC = () => {
  const navigate = useNavigate();
  const [cafes, setCafes] = useState<PartnerCafe[]>([]);
  const [cafeStats, setCafeStats] = useState<CafeStats>({ total: 0, premium: 0, withOwner: 0, withoutOwner: 0 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priceFilter, setPriceFilter] = useState('all');
  const [premiumFilter, setPremiumFilter] = useState('all');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [selectedCafe, setSelectedCafe] = useState<PartnerCafe | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' | 'info' });
  const [locationTab, setLocationTab] = useState(0);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [qrCodeData, setQrCodeData] = useState<{ qrCodeUrl: string; cafeName: string; trackingUrl: string } | null>(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [googleMapsLink, setGoogleMapsLink] = useState('');
  const [linkParsing, setLinkParsing] = useState(false);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [selectedOwner, setSelectedOwner] = useState<{ id: string; name: string; email: string } | null>(null);
  const [resetPasswordForm, setResetPasswordForm] = useState({ sendEmail: true });
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState('');

  const [franchises, setFranchises] = useState<DropdownFranchise[]>([]);

  const [cafeForm, setCafeForm] = useState({
    name: '',
    latitude: 0,
    longitude: 0,
    address: '',
    priceLevel: 2,
    isActive: true,
    allowsAnonymousMatching: true,
    photoURLs: [] as string[],
    features: [] as string[],
    franchiseId: '',
    // Cafe owner bilgileri
    createOwnerAccount: false,
    ownerEmail: '',
    ownerName: '',
    ownerPhone: ''
  });

  const fetchCafes = React.useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');

      const params: any = {};
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      const response = await axios.get('https://api.sobcof.com/api/partner-cafes', {
        headers: { 'Authorization': `Bearer ${token}` },
        params
      });

      if (response.data.success) {
        setCafes(response.data.data);
        if (response.data.stats) {
          setCafeStats(response.data.stats);
        }
      }
    } catch (error) {
      console.error('Kafeler alınırken hata:', error);
      setSnackbar({
        open: true,
        message: 'Kafeler alınırken hata oluştu',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  const fetchFranchises = React.useCallback(async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get('https://api.sobcof.com/api/franchises', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.data.success) {
        setFranchises(response.data.data);
      }
    } catch (error) {
      console.error('Franchise listesi alınırken hata:', error);
    }
  }, []);

  useEffect(() => {
    fetchCafes();
    fetchFranchises();
  }, [fetchCafes, fetchFranchises]);

  const handleAddCafe = () => {
    setCafeForm({
      name: '',
      latitude: 0,
      longitude: 0,
      address: '',
      priceLevel: 2,
      isActive: true,
      allowsAnonymousMatching: true,
      photoURLs: [],
      features: [],
      franchiseId: '',
      createOwnerAccount: false,
      ownerEmail: '',
      ownerName: '',
      ownerPhone: ''
    });
    setSelectedCafe(null);
    setLocationTab(0);
    setGoogleMapsLink('');
    setAddDialogOpen(true);
  };

  const handleCreateTestCampaign = async (cafe: PartnerCafe) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(`https://api.sobcof.com/api/partner-cafes/${cafe.id}/test-campaign`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setSnackbar({
          open: true,
          message: `${cafe.name} için test kampanyası başarıyla oluşturuldu!`,
          severity: 'success'
        });
      } else {
        throw new Error(response.data.error || 'Kampanya oluşturulamadı');
      }
    } catch (error: any) {
      console.error('Test kampanyası oluşturulurken hata:', error);
      setSnackbar({
        open: true,
        message: `Hata: ${error.response?.data?.error || error.message || 'Kampanya oluşturulamadı'}`,
        severity: 'error'
      });
    }
  };

  const handleCreateTestEvent = async (cafe: PartnerCafe) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(`https://api.sobcof.com/api/partner-cafes/${cafe.id}/test-event`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setSnackbar({
          open: true,
          message: `${cafe.name} için test etkinliği başarıyla oluşturuldu!`,
          severity: 'success'
        });
      } else {
        throw new Error(response.data.error || 'Etkinlik oluşturulamadı');
      }
    } catch (error: any) {
      console.error('Test etkinliği oluşturulurken hata:', error);
      setSnackbar({
        open: true,
        message: `Hata: ${error.response?.data?.error || error.message || 'Etkinlik oluşturulamadı'}`,
        severity: 'error'
      });
    }
  };

  const handleLocationSelect = (lat: number, lng: number) => {
    setCafeForm(prev => ({
      ...prev,
      latitude: lat,
      longitude: lng
    }));
  };

  const handleParseGoogleMapsLink = async () => {
    if (!googleMapsLink.trim()) {
      setSnackbar({
        open: true,
        message: 'Lütfen bir Google Maps linki girin',
        severity: 'error'
      });
      return;
    }

    try {
      setLinkParsing(true);
      const token = localStorage.getItem('adminToken');

      const response = await axios.post('https://api.sobcof.com/api/parse-google-maps-link',
        { url: googleMapsLink },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.data.success) {
        setCafeForm(prev => ({
          ...prev,
          latitude: response.data.data.latitude,
          longitude: response.data.data.longitude
        }));
        setSnackbar({
          open: true,
          message: `Koordinatlar bulundu: ${response.data.data.latitude.toFixed(6)}, ${response.data.data.longitude.toFixed(6)}`,
          severity: 'success'
        });
      }
    } catch (error: any) {
      console.error('Google Maps link parse hatası:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Linkten koordinat çıkarılamadı',
        severity: 'error'
      });
    } finally {
      setLinkParsing(false);
    }
  };

  const handlePhotosChange = (newPhotoURLs: string[]) => {
    setCafeForm(prev => ({
      ...prev,
      photoURLs: newPhotoURLs
    }));
  };

  const handleEditCafe = (cafe: PartnerCafe) => {
    setSelectedCafe(cafe);
    setCafeForm({
      name: cafe.name || '',
      latitude: cafe.latitude || 0,
      longitude: cafe.longitude || 0,
      address: cafe.address || '',
      priceLevel: cafe.priceLevel || 2,
      isActive: cafe.isActive,
      allowsAnonymousMatching: cafe.allowsAnonymousMatching !== false,
      photoURLs: cafe.photoURLs || [],
      features: cafe.features || [],
      franchiseId: cafe.franchiseId || '',
      createOwnerAccount: false,
      ownerEmail: '',
      ownerName: '',
      ownerPhone: ''
    });
    setLocationTab(0);
    setGoogleMapsLink('');
    setEditDialogOpen(true);
  };

  const handleSaveCafe = async () => {
    try {
      if (!cafeForm.name || !cafeForm.address) {
        setSnackbar({
          open: true,
          message: 'Kafe adı ve adres alanları zorunludur',
          severity: 'error'
        });
        return;
      }

      // Cafe owner hesabı oluşturulacaksa validation
      if (cafeForm.createOwnerAccount) {
        if (!cafeForm.ownerEmail || !cafeForm.ownerEmail.includes('@')) {
          setSnackbar({
            open: true,
            message: 'Geçerli bir email adresi giriniz',
            severity: 'error'
          });
          return;
        }
        if (!cafeForm.ownerName || cafeForm.ownerName.trim() === '') {
          setSnackbar({
            open: true,
            message: 'Kafe sahibi adı giriniz',
            severity: 'error'
          });
          return;
        }
      }

      const token = localStorage.getItem('adminToken');
      const cafeData = {
        name: cafeForm.name,
        latitude: cafeForm.latitude,
        longitude: cafeForm.longitude,
        address: cafeForm.address,
        priceLevel: cafeForm.priceLevel,
        isActive: cafeForm.isActive,
        allowsAnonymousMatching: cafeForm.allowsAnonymousMatching,
        photoURLs: cafeForm.photoURLs,
        features: cafeForm.features,
        franchiseId: cafeForm.franchiseId || null,
        // Owner bilgileri (hem yeni kafe eklerken hem de düzenlerken)
        ...(cafeForm.createOwnerAccount ? {
          createOwnerAccount: true,
          ownerEmail: cafeForm.ownerEmail,
          ownerName: cafeForm.ownerName,
          ownerPhone: cafeForm.ownerPhone
        } : {})
      };

      if (selectedCafe) {
        // Update existing cafe
        const response = await axios.put(`https://api.sobcof.com/api/partner-cafes/${selectedCafe.id}`, cafeData, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        let successMessage = 'Kafe başarıyla güncellendi';

        // Owner hesabı oluşturulduysa bilgilendir
        if (response.data.ownerAccount) {
          if (response.data.ownerAccount.error) {
            successMessage += '. Ancak kafe sahibi hesabı oluşturulamadı.';
          } else {
            successMessage += '. Kafe sahibi hesabı oluşturuldu ve giriş bilgileri email ile gönderildi.';
          }
        }

        setSnackbar({
          open: true,
          message: successMessage,
          severity: 'success'
        });
        setEditDialogOpen(false);
      } else {
        // Add new cafe
        const response = await axios.post('https://api.sobcof.com/api/partner-cafes', cafeData, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        let successMessage = 'Yeni kafe başarıyla eklendi';

        // Owner hesabı oluşturulduysa bilgilendir
        if (response.data.ownerAccount) {
          if (response.data.ownerAccount.error) {
            successMessage += '. Ancak kafe sahibi hesabı oluşturulamadı.';
          } else {
            successMessage += '. Kafe sahibi hesabı oluşturuldu ve giriş bilgileri email ile gönderildi.';
          }
        }

        setSnackbar({
          open: true,
          message: successMessage,
          severity: 'success'
        });
        setAddDialogOpen(false);
      }

      fetchCafes();
    } catch (error: any) {
      console.error('Kafe kaydedilirken hata:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Kafe kaydedilirken hata oluştu',
        severity: 'error'
      });
    }
  };

  const handleDeleteCafe = async (cafe: PartnerCafe) => {
    try {
      const token = localStorage.getItem('adminToken');

      await axios.put(`https://api.sobcof.com/api/partner-cafes/${cafe.id}`, {
        ...cafe,
        isActive: false
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setSnackbar({
        open: true,
        message: 'Kafe pasif hale getirildi',
        severity: 'success'
      });

      fetchCafes();
    } catch (error) {
      console.error('Kafe silinirken hata:', error);
      setSnackbar({
        open: true,
        message: 'Kafe silinirken hata oluştu',
        severity: 'error'
      });
    }
  };

  const handleShowQRCode = async (cafe: PartnerCafe) => {
    try {
      setQrLoading(true);
      setQrDialogOpen(true);
      const token = localStorage.getItem('adminToken');

      const response = await axios.get(`https://api.sobcof.com/api/partner-cafes/${cafe.id}/qr-code`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        setQrCodeData({
          qrCodeUrl: response.data.data.qrCodeUrl,
          cafeName: response.data.data.cafeName,
          trackingUrl: response.data.data.trackingUrl
        });
      }
    } catch (error) {
      console.error('QR kod alınırken hata:', error);
      setSnackbar({
        open: true,
        message: 'QR kod oluşturulamadı',
        severity: 'error'
      });
      setQrDialogOpen(false);
    } finally {
      setQrLoading(false);
    }
  };

  const handleDownloadQRCode = () => {
    if (!qrCodeData) return;

    const link = document.createElement('a');
    link.download = `${qrCodeData.cafeName.replace(/\s+/g, '-')}-QR.png`;
    link.href = qrCodeData.qrCodeUrl;
    link.click();
  };

  const handleResetPassword = (cafe: PartnerCafe) => {
    if (!cafe.hasOwner || !cafe.ownerInfo) {
      setSnackbar({
        open: true,
        message: 'Bu kafenin sahibi yok',
        severity: 'error'
      });
      return;
    }

    setSelectedOwner({
      id: cafe.ownerInfo.ownerId,
      name: cafe.ownerInfo.ownerName,
      email: cafe.ownerInfo.ownerEmail
    });
    setResetPasswordForm({ sendEmail: true });
    setResetPasswordDialogOpen(true);
  };

  const handleConfirmResetPassword = async () => {
    if (!selectedOwner) return;

    try {
      setIsResettingPassword(true);
      const token = localStorage.getItem('adminToken');

      const response = await axios.post(`https://api.sobcof.com/api/cafe-owners/${selectedOwner.id}/reset-password`, {
        sendEmail: resetPasswordForm.sendEmail
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        setResetPasswordDialogOpen(false);

        // E-posta gönderilmediyse şifreyi modal ile göster
        if (!resetPasswordForm.sendEmail && response.data.data.newPassword) {
          setGeneratedPassword(response.data.data.newPassword);
          setShowPasswordModal(true);
        } else {
          setSnackbar({
            open: true,
            message: 'Şifre başarıyla sıfırlandı ve kafe sahibine e-posta gönderildi!',
            severity: 'success'
          });
        }
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      setSnackbar({
        open: true,
        message: 'Şifre sıfırlama işlemi başarısız',
        severity: 'error'
      });
    } finally {
      setIsResettingPassword(false);
    }
  };

  const getPriceLevelText = (level: number) => {
    const levels = ['Çok Uygun', 'Uygun', 'Orta', 'Pahalı', 'Çok Pahalı'];
    return levels[level] || 'Bilinmiyor';
  };

  const getPriceLevelColor = (level: number) => {
    const colors = ['success', 'info', 'warning', 'error', 'default'] as const;
    return colors[level] || 'default';
  };

  const filteredCafes = cafes.filter(cafe => {
    const matchesSearch = cafe.name.toLowerCase().includes(search.toLowerCase()) ||
      cafe.address.toLowerCase().includes(search.toLowerCase());
    const matchesPrice = priceFilter === 'all' || cafe.priceLevel.toString() === priceFilter;
    const matchesPremium = premiumFilter === 'all' ||
      (premiumFilter === 'premium' && cafe.isPremium) ||
      (premiumFilter === 'not_premium' && !cafe.isPremium) ||
      (premiumFilter === 'has_owner' && cafe.hasOwner) ||
      (premiumFilter === 'no_owner' && !cafe.hasOwner);
    return matchesSearch && matchesPrice && matchesPremium;
  });

  return (
    <Box sx={{ flexGrow: 1, backgroundColor: '#f8fafc' }}>
      {/* Enhanced App Bar */}
      <AppBar
        position="static"
        sx={{
          background: 'linear-gradient(135deg, #8B4513 0%, #D2691E 100%)',
          boxShadow: '0 4px 20px rgba(139, 69, 19, 0.3)'
        }}
      >
        <Toolbar sx={{ minHeight: 70 }}>
          <IconButton color="inherit" onClick={() => navigate('/admin/dashboard')} size="large">
            <ArrowBack />
          </IconButton>
          <Avatar sx={{
            bgcolor: 'rgba(255,255,255,0.2)',
            mr: 2,
            width: 40,
            height: 40
          }}>
            <Coffee sx={{ fontSize: 20 }} />
          </Avatar>
          <Typography variant="h5" component="div" sx={{
            flexGrow: 1,
            fontWeight: 600,
            color: 'white'
          }}>
            Partner Kafeler
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleAddCafe}
            sx={{
              bgcolor: 'rgba(255,255,255,0.2)',
              color: 'white',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
            }}
          >
            Yeni Kafe Ekle
          </Button>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 3 }}>
        {/* Enhanced Filters */}
        <Card elevation={0} sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          border: '1px solid #e2e8f0',
          background: 'white'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <FilterList sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Filtreler
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
            <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
              <TextField
                label="Kafe Ara"
                variant="outlined"
                size="medium"
                fullWidth
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                  endAdornment: search && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearch('')}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2
                  }
                }}
              />
            </Box>

            <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
              <FormControl fullWidth size="medium">
                <InputLabel>Durum</InputLabel>
                <Select
                  value={statusFilter}
                  label="Durum"
                  onChange={(e) => setStatusFilter(e.target.value)}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="all">Tümü</MenuItem>
                  <MenuItem value="active">Aktif</MenuItem>
                  <MenuItem value="inactive">Pasif</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
              <FormControl fullWidth size="medium">
                <InputLabel>Fiyat Seviyesi</InputLabel>
                <Select
                  value={priceFilter}
                  label="Fiyat Seviyesi"
                  onChange={(e) => setPriceFilter(e.target.value)}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="all">Tümü</MenuItem>
                  <MenuItem value="0">Çok Uygun</MenuItem>
                  <MenuItem value="1">Uygun</MenuItem>
                  <MenuItem value="2">Orta</MenuItem>
                  <MenuItem value="3">Pahalı</MenuItem>
                  <MenuItem value="4">Çok Pahalı</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
              <FormControl fullWidth size="medium">
                <InputLabel>Premium Durumu</InputLabel>
                <Select
                  value={premiumFilter}
                  label="Premium Durumu"
                  onChange={(e) => setPremiumFilter(e.target.value)}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="all">Tümü</MenuItem>
                  <MenuItem value="premium">Premium</MenuItem>
                  <MenuItem value="not_premium">Premium Değil</MenuItem>
                  <MenuItem value="has_owner">Sahibi Var</MenuItem>
                  <MenuItem value="no_owner">Sahibi Yok</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={fetchCafes}
                fullWidth
                sx={{ borderRadius: 2, py: 1.5 }}
              >
                Yenile
              </Button>
            </Box>
          </Box>
        </Card>

        {/* Stats Cards */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
          <Box sx={{ flex: '1 1 200px', minWidth: '180px' }}>
            <Card elevation={0} sx={{
              p: 2,
              textAlign: 'center',
              borderRadius: 3,
              border: '1px solid #e2e8f0',
              background: 'linear-gradient(135deg, #8B4513 0%, #D2691E 100%)',
              color: 'white'
            }}>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                {cafeStats.total}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Toplam Kafe
              </Typography>
            </Card>
          </Box>

          <Box sx={{ flex: '1 1 200px', minWidth: '180px' }}>
            <Card elevation={0} sx={{
              p: 2,
              textAlign: 'center',
              borderRadius: 3,
              border: '1px solid #e2e8f0',
              background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
              color: 'white'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
                <Star sx={{ fontSize: 28 }} />
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {cafeStats.premium}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Premium Kafe
              </Typography>
            </Card>
          </Box>

          <Box sx={{ flex: '1 1 200px', minWidth: '180px' }}>
            <Card elevation={0} sx={{
              p: 2,
              textAlign: 'center',
              borderRadius: 3,
              border: '1px solid #e2e8f0',
              background: 'linear-gradient(135deg, #4CAF50 0%, #81C784 100%)',
              color: 'white'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
                <Person sx={{ fontSize: 28 }} />
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {cafeStats.withOwner}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Sahibi Var
              </Typography>
            </Card>
          </Box>

          <Box sx={{ flex: '1 1 200px', minWidth: '180px' }}>
            <Card elevation={0} sx={{
              p: 2,
              textAlign: 'center',
              borderRadius: 3,
              border: '1px solid #e2e8f0',
              background: 'linear-gradient(135deg, #D2691E 0%, #CD853F 100%)',
              color: 'white'
            }}>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                {filteredCafes.filter(c => c.isActive).length}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Aktif Kafe
              </Typography>
            </Card>
          </Box>

          <Box sx={{ flex: '1 1 200px', minWidth: '180px' }}>
            <Card elevation={0} sx={{
              p: 2,
              textAlign: 'center',
              borderRadius: 3,
              border: '1px solid #e2e8f0',
              background: 'linear-gradient(135deg, #9E9E9E 0%, #BDBDBD 100%)',
              color: 'white'
            }}>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                {cafeStats.withoutOwner}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Sahibi Yok
              </Typography>
            </Card>
          </Box>
        </Box>

        {/* Enhanced Cafes Table */}
        <Card elevation={0} sx={{
          borderRadius: 3,
          border: '1px solid #e2e8f0',
          overflow: 'hidden'
        }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>Kafe Adı</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>Premium</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>Kafe Sahibi</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>Adres</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>Fiyat Seviyesi</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>Durum</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>Eklenme Tarihi</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>İşlemler</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  Array.from(new Array(5)).map((_, index) => (
                    <TableRow key={index}>
                      <TableCell><Skeleton variant="text" width={120} /></TableCell>
                      <TableCell><Skeleton variant="text" width={80} /></TableCell>
                      <TableCell><Skeleton variant="text" width={120} /></TableCell>
                      <TableCell><Skeleton variant="text" width={180} /></TableCell>
                      <TableCell><Skeleton variant="text" width={80} /></TableCell>
                      <TableCell><Skeleton variant="text" width={80} /></TableCell>
                      <TableCell><Skeleton variant="text" width={100} /></TableCell>
                      <TableCell><Skeleton variant="text" width={100} /></TableCell>
                    </TableRow>
                  ))
                ) : (
                  filteredCafes
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((cafe) => (
                      <TableRow
                        key={cafe.id}
                        sx={{
                          '&:hover': { backgroundColor: '#f8fafc' },
                          transition: 'background-color 0.2s'
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar
                              sx={{
                                bgcolor: cafe.isPremium ? '#FFD700' : (cafe.isActive ? 'primary.main' : 'grey.400'),
                                width: 40,
                                height: 40
                              }}
                            >
                              {cafe.isPremium ? <Star /> : <Coffee />}
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                {cafe.name}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {cafe.isPremium ? (
                            <Chip
                              icon={<Star sx={{ color: '#FFD700 !important' }} />}
                              label="Premium"
                              size="small"
                              sx={{
                                fontWeight: 600,
                                bgcolor: '#FFF8E1',
                                color: '#F57C00',
                                border: '1px solid #FFD700'
                              }}
                            />
                          ) : cafe.hasOwner ? (
                            <Chip
                              icon={<StarBorder />}
                              label="Ücretsiz"
                              size="small"
                              color="default"
                              sx={{ fontWeight: 600 }}
                            />
                          ) : (
                            <Typography variant="body2" color="text.secondary">-</Typography>
                          )}
                          {cafe.ownerInfo?.subscriptionEndDate && cafe.isPremium && (
                            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                              {new Date(cafe.ownerInfo.subscriptionEndDate).toLocaleDateString('tr-TR')}'e kadar
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {cafe.hasOwner && cafe.ownerInfo ? (
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {cafe.ownerInfo.ownerName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {cafe.ownerInfo.ownerEmail}
                              </Typography>
                            </Box>
                          ) : (
                            <Chip
                              label="Sahip Yok"
                              size="small"
                              color="warning"
                              variant="outlined"
                              sx={{ fontWeight: 500 }}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500, maxWidth: 180 }}>
                            {cafe.address}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getPriceLevelText(cafe.priceLevel)}
                            size="small"
                            color={getPriceLevelColor(cafe.priceLevel)}
                            sx={{ fontWeight: 600 }}
                            icon={<AttachMoney />}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={cafe.isActive ? 'Aktif' : 'Pasif'}
                            size="small"
                            color={cafe.isActive ? 'success' : 'default'}
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {cafe.addedDate ?
                              new Date(cafe.addedDate).toLocaleDateString('tr-TR') :
                              'Tarih yok'
                            }
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <IconButton
                              size="small"
                              color="secondary"
                              onClick={() => handleShowQRCode(cafe)}
                              sx={{
                                bgcolor: 'secondary.50',
                                '&:hover': { bgcolor: 'secondary.100' }
                              }}
                              title="QR Kodu Görüntüle"
                            >
                              <QrCode2 fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleEditCafe(cafe)}
                              sx={{
                                bgcolor: 'primary.50',
                                '&:hover': { bgcolor: 'primary.100' }
                              }}
                              title="Kafeyi Düzenle"
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleCreateTestCampaign(cafe)}
                              sx={{
                                color: '#8B4513',
                                bgcolor: '#efebe9',
                                '&:hover': { bgcolor: '#d7ccc8' }
                              }}
                              title="Test Kampanyası Ekle"
                            >
                              <LocalOffer fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleCreateTestEvent(cafe)}
                              sx={{
                                color: '#D2691E',
                                bgcolor: '#fff3e0',
                                '&:hover': { bgcolor: '#ffe0b2' }
                              }}
                              title="Test Etkinliği Ekle"
                            >
                              <Event fontSize="small" />
                            </IconButton>
                            {cafe.hasOwner && cafe.ownerInfo && (
                              <IconButton
                                size="small"
                                color="warning"
                                onClick={() => handleResetPassword(cafe)}
                                sx={{
                                  bgcolor: 'warning.50',
                                  '&:hover': { bgcolor: 'warning.100' }
                                }}
                                title="Şifreyi Sıfırla"
                              >
                                <LockReset fontSize="small" />
                              </IconButton>
                            )}
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteCafe(cafe)}
                              sx={{
                                bgcolor: 'error.50',
                                '&:hover': { bgcolor: 'error.100' }
                              }}
                              title="Kafeyi Pasif Yap"
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={filteredCafes.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            labelRowsPerPage="Sayfa başına satır:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
            sx={{
              borderTop: '1px solid #e2e8f0',
              '& .MuiTablePagination-select': { borderRadius: 1 }
            }}
          />
        </Card>
      </Box>

      {/* Add Cafe Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{
          pb: 1,
          borderBottom: '1px solid #e2e8f0',
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <Avatar sx={{ bgcolor: 'primary.main' }}>
            <Coffee />
          </Avatar>
          <Box>
            <Typography variant="h6">Yeni Kafe Ekle</Typography>
            <Typography variant="body2" color="text.secondary">
              Partner kafe bilgilerini girin
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box>
              <TextField
                label="Kafe Adı *"
                value={cafeForm.name}
                onChange={(e) => setCafeForm({ ...cafeForm, name: e.target.value })}
                fullWidth
                variant="outlined"
              />
            </Box>

            <Box>
              <FormControl fullWidth variant="outlined">
                <InputLabel id="add-cafe-franchise-label">Franchise / Zincir Grubu</InputLabel>
                <Select
                  labelId="add-cafe-franchise-label"
                  value={cafeForm.franchiseId || ''}
                  label="Franchise / Zincir Grubu"
                  onChange={(e) => setCafeForm({ ...cafeForm, franchiseId: e.target.value })}
                >
                  <MenuItem value=""><em>Bağımsız Kafe (Zincir Değil)</em></MenuItem>
                  {franchises.map((f) => (
                    <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box>
              <Tabs
                value={locationTab}
                onChange={(_, newValue) => setLocationTab(newValue)}
                sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
              >
                <Tab label="Manuel Giriş" />
                <Tab label="Haritadan Seç" />
                <Tab label="Google Maps Linki" icon={<LinkIcon fontSize="small" />} iconPosition="start" />
              </Tabs>

              {locationTab === 0 && (
                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                  <TextField
                    label="Enlem (Latitude) *"
                    type="number"
                    value={cafeForm.latitude}
                    onChange={(e) => setCafeForm({ ...cafeForm, latitude: parseFloat(e.target.value) || 0 })}
                    fullWidth
                    variant="outlined"
                    inputProps={{ step: 0.000001 }}
                  />
                  <TextField
                    label="Boylam (Longitude) *"
                    type="number"
                    value={cafeForm.longitude}
                    onChange={(e) => setCafeForm({ ...cafeForm, longitude: parseFloat(e.target.value) || 0 })}
                    fullWidth
                    variant="outlined"
                    inputProps={{ step: 0.000001 }}
                  />
                </Box>
              )}
              {locationTab === 1 && (
                <Box sx={{ mt: 2 }}>
                  <MapSelector
                    initialLat={cafeForm.latitude || 41.0082}
                    initialLng={cafeForm.longitude || 28.9784}
                    onLocationSelect={handleLocationSelect}
                  />
                </Box>
              )}
              {locationTab === 2 && (
                <Box sx={{ mt: 2 }}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Google Maps'ten kafenin konumunu bulun, "Paylaş" butonuna tıklayın ve linki buraya yapıştırın.
                  </Alert>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    <TextField
                      label="Google Maps Linki"
                      value={googleMapsLink}
                      onChange={(e) => setGoogleMapsLink(e.target.value)}
                      fullWidth
                      variant="outlined"
                      placeholder="https://maps.google.com/... veya https://goo.gl/maps/..."
                      helperText="Örnek: https://maps.app.goo.gl/abc123 veya https://www.google.com/maps/place/..."
                    />
                    <Button
                      variant="contained"
                      onClick={handleParseGoogleMapsLink}
                      disabled={linkParsing || !googleMapsLink.trim()}
                      sx={{
                        minWidth: 120,
                        height: 56,
                        background: 'linear-gradient(135deg, #8B4513 0%, #D2691E 100%)'
                      }}
                    >
                      {linkParsing ? 'Aranıyor...' : 'Koordinat Al'}
                    </Button>
                  </Box>
                  {(cafeForm.latitude !== 0 || cafeForm.longitude !== 0) && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: '#f0f7ff', borderRadius: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                        Bulunan Koordinatlar:
                      </Typography>
                      <Typography variant="body2">
                        Enlem: {cafeForm.latitude.toFixed(6)}, Boylam: {cafeForm.longitude.toFixed(6)}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Box>

            <Box>
              <TextField
                label="Adres *"
                value={cafeForm.address}
                onChange={(e) => setCafeForm({ ...cafeForm, address: e.target.value })}
                fullWidth
                multiline
                rows={3}
                variant="outlined"
              />
            </Box>

            <Box>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                Fiyat Seviyesi: {getPriceLevelText(cafeForm.priceLevel)}
              </Typography>
              <Slider
                value={cafeForm.priceLevel}
                onChange={(_, value) => setCafeForm({ ...cafeForm, priceLevel: value as number })}
                min={0}
                max={4}
                step={1}
                marks={[
                  { value: 0, label: 'Çok Uygun' },
                  { value: 1, label: 'Uygun' },
                  { value: 2, label: 'Orta' },
                  { value: 3, label: 'Pahalı' },
                  { value: 4, label: 'Çok Pahalı' }
                ]}
                sx={{
                  '& .MuiSlider-thumb': {
                    bgcolor: 'primary.main'
                  },
                  '& .MuiSlider-track': {
                    bgcolor: 'primary.main'
                  }
                }}
              />
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Anonim Eşleşme
              </Typography>
              <Chip
                label={cafeForm.allowsAnonymousMatching ? 'Açık' : 'Kapalı'}
                color={cafeForm.allowsAnonymousMatching ? 'success' : 'error'}
                onClick={() => setCafeForm({ ...cafeForm, allowsAnonymousMatching: !cafeForm.allowsAnonymousMatching })}
                clickable
                sx={{ fontWeight: 600 }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {cafeForm.allowsAnonymousMatching
                  ? 'Kullanıcılar bu kafede anonim eşleşme yapabilir'
                  : 'Kullanıcılar sadece yol tarifi alabilir'}
              </Typography>
            </Box>

            <Box>
              <PhotoManager
                cafeId={selectedCafe?.id || 'temp_' + Date.now()}
                photoURLs={cafeForm.photoURLs}
                onPhotosChange={handlePhotosChange}
                maxPhotos={10}
              />
            </Box>

            {/* Features Selection */}
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                Kafe Özellikleri
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Kafe'nizde bulunan özellikleri seçin (Maksimum {MAX_FEATURES} özellik)
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {CAFE_FEATURES.map((feature) => {
                  const currentFeatures = cafeForm.features || [];
                  const isSelected = currentFeatures.includes(feature.key);
                  const canSelect = isSelected || currentFeatures.length < MAX_FEATURES;

                  return (
                    <Chip
                      key={feature.key}
                      label={`${feature.icon} ${feature.label}`}
                      onClick={() => {
                        if (!canSelect && !isSelected) return;
                        const newFeatures = isSelected
                          ? currentFeatures.filter(f => f !== feature.key)
                          : [...currentFeatures, feature.key];
                        setCafeForm({ ...cafeForm, features: newFeatures });
                      }}
                      color={isSelected ? 'primary' : 'default'}
                      variant={isSelected ? 'filled' : 'outlined'}
                      disabled={!canSelect && !isSelected}
                      clickable={canSelect || isSelected}
                      sx={{
                        fontWeight: isSelected ? 600 : 400,
                        opacity: (!canSelect && !isSelected) ? 0.5 : 1
                      }}
                    />
                  );
                })}
              </Box>
              {cafeForm.features && cafeForm.features.length > 0 && (
                <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 1 }}>
                  {cafeForm.features.length}/{MAX_FEATURES} özellik seçildi
                </Typography>
              )}
            </Box>

            {/* Cafe Owner Account Section */}
            <Divider sx={{ my: 2 }} />

            <Box>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                Kafe Sahibi Hesabı
              </Typography>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={cafeForm.createOwnerAccount}
                    onChange={(e) => setCafeForm({ ...cafeForm, createOwnerAccount: e.target.checked })}
                  />
                }
                label="Kafe sahibi için giriş hesabı oluştur"
              />

              {cafeForm.createOwnerAccount && (
                <Box sx={{ mt: 2, pl: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label="Email *"
                    type="email"
                    value={cafeForm.ownerEmail}
                    onChange={(e) => setCafeForm({ ...cafeForm, ownerEmail: e.target.value })}
                    fullWidth
                    variant="outlined"
                    helperText="Giriş bilgileri bu adrese gönderilecek"
                  />
                  <TextField
                    label="İsim Soyisim *"
                    value={cafeForm.ownerName}
                    onChange={(e) => setCafeForm({ ...cafeForm, ownerName: e.target.value })}
                    fullWidth
                    variant="outlined"
                  />
                  <TextField
                    label="Telefon"
                    value={cafeForm.ownerPhone}
                    onChange={(e) => setCafeForm({ ...cafeForm, ownerPhone: e.target.value })}
                    fullWidth
                    variant="outlined"
                    helperText="Opsiyonel"
                  />
                  <Alert severity="info" sx={{ mt: 1 }}>
                    Otomatik şifre oluşturulacak ve email ile gönderilecektir.
                  </Alert>
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 1, borderTop: '1px solid #e2e8f0' }}>
          <Button
            onClick={() => setAddDialogOpen(false)}
            variant="outlined"
            sx={{ borderRadius: 2, px: 3 }}
          >
            İptal
          </Button>
          <Button
            onClick={handleSaveCafe}
            variant="contained"
            sx={{
              borderRadius: 2,
              px: 3,
              background: 'linear-gradient(135deg, #8B4513 0%, #D2691E 100%)'
            }}
          >
            Kaydet
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Cafe Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{
          pb: 1,
          borderBottom: '1px solid #e2e8f0',
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <Avatar sx={{ bgcolor: 'primary.main' }}>
            <Coffee />
          </Avatar>
          <Box>
            <Typography variant="h6">Kafe Düzenle</Typography>
            {selectedCafe && (
              <Typography variant="body2" color="text.secondary">
                {selectedCafe.name}
              </Typography>
            )}
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box>
              <TextField
                label="Kafe Adı *"
                value={cafeForm.name}
                onChange={(e) => setCafeForm({ ...cafeForm, name: e.target.value })}
                fullWidth
                variant="outlined"
              />
            </Box>

            <Box>
              <FormControl fullWidth variant="outlined">
                <InputLabel id="edit-cafe-franchise-label">Franchise / Zincir Grubu</InputLabel>
                <Select
                  labelId="edit-cafe-franchise-label"
                  value={cafeForm.franchiseId || ''}
                  label="Franchise / Zincir Grubu"
                  onChange={(e) => setCafeForm({ ...cafeForm, franchiseId: e.target.value })}
                >
                  <MenuItem value=""><em>Bağımsız Kafe (Zincir Değil)</em></MenuItem>
                  {franchises.map((f) => (
                    <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box>
              <Tabs
                value={locationTab}
                onChange={(_, newValue) => setLocationTab(newValue)}
                sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
              >
                <Tab label="Manuel Giriş" />
                <Tab label="Haritadan Seç" />
                <Tab label="Google Maps Linki" icon={<LinkIcon fontSize="small" />} iconPosition="start" />
              </Tabs>

              {locationTab === 0 && (
                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                  <TextField
                    label="Enlem (Latitude) *"
                    type="number"
                    value={cafeForm.latitude}
                    onChange={(e) => setCafeForm({ ...cafeForm, latitude: parseFloat(e.target.value) || 0 })}
                    fullWidth
                    variant="outlined"
                    inputProps={{ step: 0.000001 }}
                  />
                  <TextField
                    label="Boylam (Longitude) *"
                    type="number"
                    value={cafeForm.longitude}
                    onChange={(e) => setCafeForm({ ...cafeForm, longitude: parseFloat(e.target.value) || 0 })}
                    fullWidth
                    variant="outlined"
                    inputProps={{ step: 0.000001 }}
                  />
                </Box>
              )}
              {locationTab === 1 && (
                <Box sx={{ mt: 2 }}>
                  <MapSelector
                    initialLat={cafeForm.latitude || 41.0082}
                    initialLng={cafeForm.longitude || 28.9784}
                    onLocationSelect={handleLocationSelect}
                  />
                </Box>
              )}
              {locationTab === 2 && (
                <Box sx={{ mt: 2 }}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Google Maps'ten kafenin konumunu bulun, "Paylaş" butonuna tıklayın ve linki buraya yapıştırın.
                  </Alert>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    <TextField
                      label="Google Maps Linki"
                      value={googleMapsLink}
                      onChange={(e) => setGoogleMapsLink(e.target.value)}
                      fullWidth
                      variant="outlined"
                      placeholder="https://maps.google.com/... veya https://goo.gl/maps/..."
                      helperText="Örnek: https://maps.app.goo.gl/abc123 veya https://www.google.com/maps/place/..."
                    />
                    <Button
                      variant="contained"
                      onClick={handleParseGoogleMapsLink}
                      disabled={linkParsing || !googleMapsLink.trim()}
                      sx={{
                        minWidth: 120,
                        height: 56,
                        background: 'linear-gradient(135deg, #8B4513 0%, #D2691E 100%)'
                      }}
                    >
                      {linkParsing ? 'Aranıyor...' : 'Koordinat Al'}
                    </Button>
                  </Box>
                  {(cafeForm.latitude !== 0 || cafeForm.longitude !== 0) && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: '#f0f7ff', borderRadius: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                        Bulunan Koordinatlar:
                      </Typography>
                      <Typography variant="body2">
                        Enlem: {cafeForm.latitude.toFixed(6)}, Boylam: {cafeForm.longitude.toFixed(6)}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Box>

            <Box>
              <TextField
                label="Adres *"
                value={cafeForm.address}
                onChange={(e) => setCafeForm({ ...cafeForm, address: e.target.value })}
                fullWidth
                multiline
                rows={3}
                variant="outlined"
              />
            </Box>

            <Box>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                Fiyat Seviyesi: {getPriceLevelText(cafeForm.priceLevel)}
              </Typography>
              <Slider
                value={cafeForm.priceLevel}
                onChange={(_, value) => setCafeForm({ ...cafeForm, priceLevel: value as number })}
                min={0}
                max={4}
                step={1}
                marks={[
                  { value: 0, label: 'Çok Uygun' },
                  { value: 1, label: 'Uygun' },
                  { value: 2, label: 'Orta' },
                  { value: 3, label: 'Pahalı' },
                  { value: 4, label: 'Çok Pahalı' }
                ]}
                sx={{
                  '& .MuiSlider-thumb': {
                    bgcolor: 'primary.main'
                  },
                  '& .MuiSlider-track': {
                    bgcolor: 'primary.main'
                  }
                }}
              />
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Durum
              </Typography>
              <Chip
                label={cafeForm.isActive ? 'Aktif' : 'Pasif'}
                color={cafeForm.isActive ? 'success' : 'default'}
                onClick={() => setCafeForm({ ...cafeForm, isActive: !cafeForm.isActive })}
                clickable
                sx={{ fontWeight: 600 }}
              />
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Anonim Eşleşme
              </Typography>
              <Chip
                label={cafeForm.allowsAnonymousMatching ? 'Açık' : 'Kapalı'}
                color={cafeForm.allowsAnonymousMatching ? 'success' : 'error'}
                onClick={() => setCafeForm({ ...cafeForm, allowsAnonymousMatching: !cafeForm.allowsAnonymousMatching })}
                clickable
                sx={{ fontWeight: 600 }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {cafeForm.allowsAnonymousMatching
                  ? 'Kullanıcılar bu kafede anonim eşleşme yapabilir'
                  : 'Kullanıcılar sadece yol tarifi alabilir'}
              </Typography>
            </Box>

            <Box>
              <PhotoManager
                cafeId={selectedCafe?.id || 'temp_' + Date.now()}
                photoURLs={cafeForm.photoURLs}
                onPhotosChange={handlePhotosChange}
                maxPhotos={10}
              />
            </Box>

            {/* Features Selection */}
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                Kafe Özellikleri
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Kafe'nizde bulunan özellikleri seçin (Maksimum {MAX_FEATURES} özellik)
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {CAFE_FEATURES.map((feature) => {
                  const currentFeatures = cafeForm.features || [];
                  const isSelected = currentFeatures.includes(feature.key);
                  const canSelect = isSelected || currentFeatures.length < MAX_FEATURES;

                  return (
                    <Chip
                      key={feature.key}
                      label={`${feature.icon} ${feature.label}`}
                      onClick={() => {
                        if (!canSelect && !isSelected) return;
                        const newFeatures = isSelected
                          ? currentFeatures.filter(f => f !== feature.key)
                          : [...currentFeatures, feature.key];
                        setCafeForm({ ...cafeForm, features: newFeatures });
                      }}
                      color={isSelected ? 'primary' : 'default'}
                      variant={isSelected ? 'filled' : 'outlined'}
                      disabled={!canSelect && !isSelected}
                      clickable={canSelect || isSelected}
                      sx={{
                        fontWeight: isSelected ? 600 : 400,
                        opacity: (!canSelect && !isSelected) ? 0.5 : 1
                      }}
                    />
                  );
                })}
              </Box>
              {cafeForm.features && cafeForm.features.length > 0 && (
                <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 1 }}>
                  {cafeForm.features.length}/{MAX_FEATURES} özellik seçildi
                </Typography>
              )}
            </Box>

            {/* Cafe Owner Account Section */}
            <Divider sx={{ my: 2 }} />

            <Box>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                Kafe Sahibi Hesabı
              </Typography>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={cafeForm.createOwnerAccount}
                    onChange={(e) => setCafeForm({ ...cafeForm, createOwnerAccount: e.target.checked })}
                  />
                }
                label="Kafe sahibi için giriş hesabı oluştur"
              />

              {cafeForm.createOwnerAccount && (
                <Box sx={{ mt: 2, pl: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label="Email *"
                    type="email"
                    value={cafeForm.ownerEmail}
                    onChange={(e) => setCafeForm({ ...cafeForm, ownerEmail: e.target.value })}
                    fullWidth
                    variant="outlined"
                    helperText="Giriş bilgileri bu adrese gönderilecek"
                  />
                  <TextField
                    label="İsim Soyisim *"
                    value={cafeForm.ownerName}
                    onChange={(e) => setCafeForm({ ...cafeForm, ownerName: e.target.value })}
                    fullWidth
                    variant="outlined"
                  />
                  <TextField
                    label="Telefon"
                    value={cafeForm.ownerPhone}
                    onChange={(e) => setCafeForm({ ...cafeForm, ownerPhone: e.target.value })}
                    fullWidth
                    variant="outlined"
                    helperText="Opsiyonel"
                  />
                  <Alert severity="info" sx={{ mt: 1 }}>
                    Otomatik şifre oluşturulacak ve email ile gönderilecektir.
                  </Alert>
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 1, borderTop: '1px solid #e2e8f0' }}>
          <Button
            onClick={() => setEditDialogOpen(false)}
            variant="outlined"
            sx={{ borderRadius: 2, px: 3 }}
          >
            İptal
          </Button>
          <Button
            onClick={handleSaveCafe}
            variant="contained"
            sx={{
              borderRadius: 2,
              px: 3,
              background: 'linear-gradient(135deg, #8B4513 0%, #D2691E 100%)'
            }}
          >
            Güncelle
          </Button>
        </DialogActions>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog
        open={qrDialogOpen}
        onClose={() => setQrDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{
          pb: 1,
          borderBottom: '1px solid #e2e8f0',
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <Avatar sx={{ bgcolor: 'secondary.main' }}>
            <QrCode2 />
          </Avatar>
          <Box>
            <Typography variant="h6">QR Kod</Typography>
            {qrCodeData && (
              <Typography variant="body2" color="text.secondary">
                {qrCodeData.cafeName}
              </Typography>
            )}
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 3, textAlign: 'center' }}>
          {qrLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
              <Skeleton variant="rectangular" width={300} height={300} />
            </Box>
          ) : qrCodeData ? (
            <Box>
              <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                mb: 3,
                p: 3,
                bgcolor: '#f8fafc',
                borderRadius: 2
              }}>
                <img
                  src={qrCodeData.qrCodeUrl}
                  alt="QR Code"
                  style={{
                    width: '100%',
                    maxWidth: 300,
                    height: 'auto',
                    border: '4px solid white',
                    borderRadius: 8,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                  }}
                />
              </Box>

              <Alert severity="info" sx={{ mb: 2, textAlign: 'left' }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                  Tracking URL:
                </Typography>
                <Typography variant="body2" sx={{ wordBreak: 'break-all', fontFamily: 'monospace' }}>
                  {qrCodeData.trackingUrl}
                </Typography>
              </Alert>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Bu QR kodu yazdırıp kafenize yerleştirebilirsiniz. Müşteriler bu QR kodu okuttuğunda uygulamayı indirme sayfasına yönlendirilecek ve tarama sayısı kaydedilecektir.
              </Typography>
            </Box>
          ) : null}
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 1, borderTop: '1px solid #e2e8f0', justifyContent: 'space-between' }}>
          <Button
            onClick={() => setQrDialogOpen(false)}
            variant="outlined"
            sx={{ borderRadius: 2, px: 3 }}
          >
            Kapat
          </Button>
          <Button
            onClick={handleDownloadQRCode}
            variant="contained"
            startIcon={<Download />}
            disabled={!qrCodeData}
            sx={{
              borderRadius: 2,
              px: 3,
              background: 'linear-gradient(135deg, #8B4513 0%, #D2691E 100%)'
            }}
          >
            QR Kodu İndir
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog
        open={resetPasswordDialogOpen}
        onClose={() => setResetPasswordDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{
          pb: 1,
          borderBottom: '1px solid #e2e8f0',
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <Avatar sx={{ bgcolor: 'warning.main' }}>
            <LockReset />
          </Avatar>
          <Box>
            <Typography variant="h6">Şifreyi Sıfırla</Typography>
            {selectedOwner && (
              <Typography variant="body2" color="text.secondary">
                {selectedOwner.name} ({selectedOwner.email})
              </Typography>
            )}
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Kafe sahibinin şifresini sıfırlamak istediğinizden emin misiniz?
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={resetPasswordForm.sendEmail}
                onChange={(e) => setResetPasswordForm({ sendEmail: e.target.checked })}
              />
            }
            label="Yeni şifreyi e-posta ile gönder"
          />
          {!resetPasswordForm.sendEmail && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              E-posta gönderilmeyecek. Yeni şifre ekranda gösterilecek ve kafe sahibine manuel olarak iletmeniz gerekecek.
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 1, borderTop: '1px solid #e2e8f0' }}>
          <Button
            onClick={() => setResetPasswordDialogOpen(false)}
            variant="outlined"
            disabled={isResettingPassword}
            sx={{ borderRadius: 2, px: 3 }}
          >
            İptal
          </Button>
          <Button
            onClick={handleConfirmResetPassword}
            variant="contained"
            disabled={isResettingPassword}
            sx={{
              borderRadius: 2,
              px: 3,
              background: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)'
            }}
          >
            {isResettingPassword ? 'Sıfırlanıyor...' : 'Şifreyi Sıfırla'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Password Display Modal */}
      <Dialog
        open={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{
          pb: 1,
          borderBottom: '1px solid #e2e8f0',
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <Avatar sx={{ bgcolor: 'success.main' }}>
            <LockReset />
          </Avatar>
          <Typography variant="h6">Yeni Şifre Oluşturuldu</Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          <Alert severity="success" sx={{ mb: 2 }}>
            Şifre başarıyla sıfırlandı!
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Lütfen bu şifreyi güvenli bir şekilde kafe sahibine iletin:
          </Typography>
          <Card sx={{
            p: 2,
            bgcolor: '#f5f5f5',
            border: '2px dashed #ccc',
            textAlign: 'center'
          }}>
            <Typography
              variant="h5"
              sx={{
                fontFamily: 'monospace',
                fontWeight: 700,
                color: 'primary.main',
                letterSpacing: 2
              }}
            >
              {generatedPassword}
            </Typography>
          </Card>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Bu şifre bir daha gösterilmeyecektir. Lütfen kaydedin veya kafe sahibine iletin.
          </Alert>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 1, borderTop: '1px solid #e2e8f0' }}>
          <Button
            onClick={() => {
              navigator.clipboard.writeText(generatedPassword);
              setSnackbar({
                open: true,
                message: 'Şifre panoya kopyalandı',
                severity: 'success'
              });
            }}
            variant="outlined"
            sx={{ borderRadius: 2, px: 3 }}
          >
            Kopyala
          </Button>
          <Button
            onClick={() => setShowPasswordModal(false)}
            variant="contained"
            sx={{
              borderRadius: 2,
              px: 3,
              background: 'linear-gradient(135deg, #8B4513 0%, #D2691E 100%)'
            }}
          >
            Kapat
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PartnerCafesPage;
